import { MouseEventHandler } from "react"
import * as React from "react"
import styled from "styled-components"
import { border, BorderProps } from "styled-system"
import { size, SizeProps } from "../../styles/styled-system"
import { colors, breakpoint, scale } from "../../styles"
import { Flex } from "../Flex"
import { Icon } from "../Icon"
import { Text } from "../Text"
import { TagStyled } from "../Card"
import { ChildrenProps } from "../../@types/@core"

export interface CardHorizontalComponent {
	Title: React.FC<ChildrenProps>
	Preamble: React.FC<ChildrenProps>
}

export interface CardHorizontalStyledProps extends BorderProps, SizeProps {}

export interface CardHorizontalProps extends CardHorizontalStyledProps {
	media?: React.ReactNode
	favButton?: React.ReactNode
	tag?: string
	children?: React.ReactNode
}

const Media = styled.figure`
	position: relative;
	background-color: ${colors.greyEE};
	aspect-ratio: 1/1;
	min-width: ${scale.px(10)};

	${breakpoint("m")`
		min-width: ${scale.px(21)};
	`}

	// Fav button
	> button {
		position: absolute;
		top: ${scale.px(1.5)};
		left: ${scale.px(1.5)};
		z-index: 2;

		svg {
			display: block;
		}
	}

	> img {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
	}
`

const ContentStyled = styled.div`
	position: relative;
	width: 100%;
	padding: ${scale.px(1.5)} ${scale.px(2.5)} ${scale.px(1.5)} ${scale.px(1)};

	${breakpoint("m")`
		padding: ${scale.px(2)} ${scale.px(4)} ${scale.px(2)} ${scale.px(2.5)};
	`}
`

const ContentInner = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	max-width: 80%;

	${breakpoint("l")`
		max-width: ${scale.px(82)};
	`}
`

const StyledFavouriteButtonContainer = styled(Flex)`
	position: absolute;
	z-index: 2;
`

const CardHorizontalStyled = styled.div<CardHorizontalStyledProps>`
	position: relative;
	display: flex;
	width: 100%;
	background-color: transparent;
	width: ${(props) => props.w};
	border-radius: ${scale.px(3)};
	overflow: hidden;
	border: 1px solid transparent;
	${size}
	${border}

	.link-arrow {
		display: none;

		position: absolute;
		right: ${scale.px(2.5)};
		top: 50%;
		transform: translateY(-50%);

		${breakpoint("m")`
			right: ${scale.px(4)};
		`}
	}

	// Show link arrow only if wrapped by <a>
	a & {
		.link-arrow {
			display: block;
		}
	}

	a:hover & {
		border: 1px solid ${colors.hoverBlue};

		.link-arrow,
		path {
			fill: ${colors.hoverBlue};
		}
	}

	${TagStyled} {
		height: ${scale.px(3)};
		padding: 0 ${scale.px(1.5)};

		${breakpoint("m")`
			height: ${scale.px(4)};
			padding: 0 ${scale.px(2)};
		`}
	}
`

const CardHorizontal: React.FC<CardHorizontalProps> & CardHorizontalComponent =
	({ children, media, tag, favButton, ...rest }) => {
		return (
			<CardHorizontalStyled {...rest}>
				<Media>
					{favButton && favButton}
					{media}
				</Media>
				<ContentStyled>
					<ContentInner>
						{children}
						{tag && <TagStyled>{tag}</TagStyled>}
					</ContentInner>
					<Icon icon="arrow-right" color="navy" className="link-arrow" />
				</ContentStyled>
			</CardHorizontalStyled>
		)
	}

const Title: React.FC<ChildrenProps> = (props) => (
	<Text
		as="h3"
		fontSize={{ _: 2, m: 3 }}
		lineHeight={{ _: 2.5, m: 4.5 }}
		fontWeight="bold"
		mb={0.7}
		wordBreak="break-word"
	>
		{props.children}
	</Text>
)

CardHorizontal.Title = Title

const Preamble: React.FC<ChildrenProps> = (props) => (
	<Text
		fontSize={{ _: 1.5, m: 2 }}
		lineHeight={{ _: 2, m: 3 }}
		truncate="twoLine"
	>
		{props.children}
	</Text>
)

CardHorizontal.Preamble = Preamble

export { CardHorizontal }
